/**
 * ddCatalog — DramaDetector catalog definition (no RN runtime dependency)
 *
 * Separated from catalog.tsx so that build-time scripts (e.g. generate-system-prompt.ts)
 * can import ddCatalog without pulling in react-native via @json-render/react-native.
 *
 * The schema import uses the /schema subpath which only depends on @json-render/core,
 * not on react-native itself.
 *
 * Task: SDUI-M.P2.0
 * Agent: Diego <diego@liftping.net>
 */

import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react-native/schema";

import {
  AnalysisCardPropsSchema,
  DramaScorePropsSchema,
  DramaCategoryBadgePropsSchema,
  NarrativeBlockPropsSchema,
  TierBadgePropsSchema,
  InsightRowPropsSchema,
  SectionDividerPropsSchema,
  ActionButtonPropsSchema,
  PatternSpotlightPropsSchema,
  TransformationInsightPropsSchema,
  StyleConsistencyInsightPropsSchema,
  CommunicationPatternInsightPropsSchema,
  ModuleProgressPropsSchema,
  UpgradeTierParamsSchema,
  ShareResultParamsSchema,
  SaveAnalysisParamsSchema,
  DismissInsightParamsSchema,
} from "./schema";

// ============================================================================
// Catalog definition
// ============================================================================

export const ddCatalog = defineCatalog(schema, {
  components: {
    AnalysisCard: {
      props: AnalysisCardPropsSchema,
      slots: ["default"],
      description:
        "Primary container card that groups related analysis content. " +
        'variant="standard" for free content, "elevated" for emphasis, "premium" for DramaX (gold border). ' +
        "Max title: 60 chars. Max subtitle: 80 chars.",
    },
    DramaScore: {
      props: DramaScorePropsSchema,
      slots: [],
      description:
        "Hero element displaying the numeric drama score (0-10) with tier-appropriate color coding. " +
        "Always place this as the first visible element. " +
        'Use size="lg" for high_drama or extreme_drama categories. ' +
        "Color: safe=green-500, caution=amber-500, high_drama=red-500, extreme_drama=red-600.",
    },
    DramaCategoryBadge: {
      props: DramaCategoryBadgePropsSchema,
      slots: [],
      description:
        "Compact badge showing the drama category (safe/caution/high_drama/extreme_drama). " +
        "Use alongside DramaScore for quick at-a-glance category identification.",
    },
    NarrativeBlock: {
      props: NarrativeBlockPropsSchema,
      slots: [],
      description:
        "Prose content block for narrative analysis text. " +
        'Use emphasis="high" for key findings, "critical" for extreme_drama content. ' +
        "Max text: 2000 chars.",
    },
    TierBadge: {
      props: TierBadgePropsSchema,
      slots: [],
      description:
        "Displays which subscription tier unlocked this content. " +
        "Use showUpgradeHint=true when content is previewed but not fully accessible.",
    },
    InsightRow: {
      props: InsightRowPropsSchema,
      slots: [],
      description:
        "Single insight item within a list. Props: title (heading), description (detail text). " +
        "Optional trend (up/down/neutral) with changePercent for trend display.",
    },
    SectionDivider: {
      props: SectionDividerPropsSchema,
      slots: [],
      description:
        "Visual horizontal separator between sections. " +
        'Use variant="subtle" between sub-sections, "emphasized" between major sections.',
    },
    ActionButton: {
      props: ActionButtonPropsSchema,
      slots: [],
      description:
        "CTA button wired to catalog actions. " +
        "action must match one of: upgrade_tier, share_result, save_analysis, dismiss_insight.",
    },
    PatternSpotlight: {
      props: PatternSpotlightPropsSchema,
      slots: [],
      description:
        "[DRAMA+ ONLY] Premium pattern analysis card showing a detected communication pattern. " +
        "Only generate for users with tier=drama_plus or tier=dramax. " +
        "Include up to 3 example quotes. Suggest a constructive reframe via suggestion.",
    },
    TransformationInsight: {
      props: TransformationInsightPropsSchema,
      slots: [],
      description:
        "[DRAMAX ONLY] Premium editorial before/after transformation card. " +
        "Only generate for users with tier=dramax. " +
        "before/after: max 600 chars each. rationale: max 400 chars.",
    },
    StyleConsistencyInsight: {
      props: StyleConsistencyInsightPropsSchema,
      slots: [],
      description:
        "[DRAMA+ ONLY] Communication style analysis showing formality shift, vocabulary diversity, " +
        "punctuation pattern, and message length. Displays as collapsible card with summary pills " +
        "and expandable metric bars.",
    },
    CommunicationPatternInsight: {
      props: CommunicationPatternInsightPropsSchema,
      slots: [],
      description:
        "[DRAMA+ ONLY] Intra-conversation style shift detection. Shows confidence bar and contributing " +
        "signals sorted by impact. Uses 'Communication Pattern' framing — NEVER use 'AI' in any text. " +
        "Only render when hasBaseline is true.",
    },
    ModuleProgress: {
      props: ModuleProgressPropsSchema,
      slots: [],
      description:
        "Progress indicator for modules building baseline data. Shows progress bar with analysis count " +
        "and encouraging text. For free users (isPremium=false), shows upgrade CTA instead of progress.",
    },
  },

  actions: {
    upgrade_tier: {
      params: UpgradeTierParamsSchema,
      description: "Opens the paywall/subscription upgrade flow.",
    },
    share_result: {
      params: ShareResultParamsSchema,
      description: "Shares the analysis result (clipboard or native share sheet).",
    },
    save_analysis: {
      params: SaveAnalysisParamsSchema,
      description: "Saves/bookmarks this analysis to the user history.",
    },
    dismiss_insight: {
      params: DismissInsightParamsSchema,
      description: "Dismisses or hides a specific insight from the current view.",
    },
  },
});
